import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { formatDays } from '../subrentals/types';
import { useUrlState } from '../nav/urlState';
import './finance.css';

type Purchase = { id: string; invoiceNumber?: string; issueDate: string; netGrosze?: number; grossGrosze?: number };
export type SettlementRow = {
  supplierId: string;
  supplierName: string;
  eventId: string;
  eventName: string;
  totalGrosze?: number;
  count: number;
  firstDay: string;
  lastDay: string;
  invoice: { state: 'brak' | 'auto' | 'reczna'; purchases: Purchase[] };
  suggestions: Purchase[];
  paid: 'zaplacona' | 'otwarta' | 'anulowana' | null;
  /** Owner 2026-11-01: the event is closed (its „Anulowane” rentals are not in the sum; `count` 0: all are) — settle with the supplier. Only with the events' „Status”. */
  eventClosed?: 'odwolane' | 'rezygnacja' | 'usuniete';
};
/** Owner 2026-11-01: every rental of the row cancelled with the event (only a linked invoice keeps the row). */
export const CANCELLED_LABELS: Record<NonNullable<SettlementRow['eventClosed']>, string> = {
  odwolane: 'Anulowano – wydarzenie odwołane',
  rezygnacja: 'Anulowano – rezygnacja klienta',
  usuniete: 'Anulowano – wydarzenie usunięte',
};
/** Owner 2026-11-01: „Wydarzenie odwołane – sprawdź rozliczenie z dostawcą” (the picked up / returned gear still costs). */
export const CLOSED_WARNINGS: Record<NonNullable<SettlementRow['eventClosed']>, string> = {
  odwolane: 'Wydarzenie odwołane – sprawdź rozliczenie z dostawcą',
  rezygnacja: 'Rezygnacja klienta – sprawdź rozliczenie z dostawcą',
  usuniete: 'Wydarzenie usunięte – sprawdź rozliczenie z dostawcą',
};
const KEY = ['finance', 'suppliers'];

const purchaseLabel = (p: Purchase) => [p.invoiceNumber ?? 'faktura', p.issueDate.split('-').reverse().join('.'), ...(p.netGrosze !== undefined ? [`${formatPln(p.netGrosze)} netto`] : [])].join(' · ');

/** "Połącz z fakturą": the supplier's purchase invoices around the event (the period's first), one to link. */
function LinkForm({ row, onDone }: { row: SettlementRow; onDone: () => void }) {
  const queryClient = useQueryClient();
  const candidates = useQuery({
    queryKey: [...KEY, 'candidates', row.eventId, row.supplierId],
    queryFn: () => api<{ purchases: Purchase[] }>(`/api/finance/suppliers/candidates?eventId=${row.eventId}&supplierId=${row.supplierId}`),
  });
  const [chosen, setChosen] = useState('');
  const [error, setError] = useState('');
  const suggested = new Set(row.suggestions.map((s) => s.id));
  const list = [...(candidates.data?.purchases ?? [])].sort((a, b) => Number(suggested.has(b.id)) - Number(suggested.has(a.id)));
  async function link() {
    if (!chosen) return setError('Wybierz fakturę');
    try {
      await api('/api/finance/suppliers/links', { method: 'POST', body: { eventId: row.eventId, supplierId: row.supplierId, purchaseId: chosen } });
      await queryClient.invalidateQueries({ queryKey: KEY });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się połączyć');
    }
  }
  return (
    <div className="sup-link">
      <label className="field">
        <span>Faktura dostawcy</span>
        <select value={chosen} onChange={(e) => setChosen(e.target.value)}>
          <option value="">{candidates.isLoading ? 'Wczytywanie…' : list.length === 0 ? 'Ten dostawca nie ma faktur zakupowych' : 'Wybierz fakturę'}</option>
          {list.map((p) => (
            <option key={p.id} value={p.id}>
              {suggested.has(p.id) ? '★ ' : ''}
              {purchaseLabel(p)}
            </option>
          ))}
        </select>
      </label>
      <div className="actions">
        <button className="btn small primary" type="button" onClick={() => void link()}>
          Połącz
        </button>
        <button className="btn small" type="button" onClick={onDone}>
          Anuluj
        </button>
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const INVOICE: Record<SettlementRow['invoice']['state'], string> = { brak: 'brak faktury', auto: 'dopasowana automatycznie', reczna: 'połączona ręcznie' };

/**
 * "Finanse → Dostawcy" (owner 2026-10-21 00:32: "rozliczenia z dostawcami musza sie pojawiac w finansach, zebym nie
 * zgubil. zsumowane wypozyczenie i data oraz czy wplynela faktura. i czy jest oplacona"): a row per supplier and
 * event. By default only what is still to settle (no invoice or not paid).
 */
export function SuppliersTab() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const year = new Date().getFullYear();
  // P13: the year and the filter live in the address
  const { params, update } = useUrlState();
  const asked = Number(params.get('rok'));
  const shownYear = Number.isInteger(asked) && asked >= 2000 && asked <= 2200 ? asked : year;
  const all = params.get('wszystkie') === '1';
  const setAll = (v: boolean) => update({ wszystkie: v ? '1' : null });
  const setShownYear = (y: number) => update({ rok: y === year ? null : String(y) });
  const years = [...new Set([year + 1, year, year - 1, year - 2, shownYear])].sort((a, b) => b - a);
  const [linking, setLinking] = useState<string | null>(null);
  const [error, setError] = useState('');
  const data = useQuery({
    queryKey: [...KEY, shownYear, all],
    queryFn: () => api<{ rows: SettlementRow[]; paidKnown: boolean }>(`/api/finance/suppliers?year=${shownYear}${all ? '&all=1' : ''}`),
  });
  const rows = data.data?.rows ?? [];
  const mayLink = !!me && can(me, 'finance.suppliers', 'edit') && can(me, 'purchases.purchases', 'view');
  const seesSums = rows.some((r) => r.totalGrosze !== undefined);
  async function unlink(r: SettlementRow, p: Purchase) {
    try {
      await api(`/api/finance/suppliers/links?eventId=${r.eventId}&supplierId=${r.supplierId}&purchaseId=${p.id}`, { method: 'DELETE' });
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się odłączyć');
    }
    await queryClient.invalidateQueries({ queryKey: KEY });
  }
  /** "To nie ta faktura": the automatic match is not made again for this row (no question asked). */
  async function dismiss(r: SettlementRow, p: Purchase) {
    try {
      await api('/api/finance/suppliers/dismissals', { method: 'POST', body: { eventId: r.eventId, supplierId: r.supplierId, purchaseId: p.id } });
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nie udało się zapisać');
    }
    await queryClient.invalidateQueries({ queryKey: KEY });
  }
  const key = (r: SettlementRow) => `${r.supplierId}:${r.eventId}`;
  return (
    <section className="page wide finance-suppliers" data-feature-id="finance.suppliers">
      <p className="lede">Dopożyczenia u dostawców na każde wydarzenie: suma, daty, faktura i płatność — żeby żadne rozliczenie nie zginęło.</p>
      <div className="filters">
        <div className="segment" role="group" aria-label="Które rozliczenia">
          <button type="button" aria-pressed={!all} onClick={() => setAll(false)}>
            Do rozliczenia
          </button>
          <button type="button" aria-pressed={all} onClick={() => setAll(true)}>
            Wszystkie
          </button>
        </div>
        <label className="field">
          <span>Rok</span>
          <select value={shownYear} onChange={(e) => setShownYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>
      {data.isLoading && (
        <p className="muted" role="status">
          Wczytywanie…
        </p>
      )}
      {data.error && <p className="alert" role="alert">{data.error instanceof ApiError ? data.error.message : 'Nie udało się wczytać rozliczeń'}</p>}
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
      {data.data && rows.length === 0 && <p className="muted">{all ? 'W tym roku nie ma dopożyczeń.' : 'Wszystko rozliczone — nie ma dopożyczeń bez faktury ani nieopłaconych.'}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table" aria-label="Rozliczenia z dostawcami">
            <thead>
              <tr>
                <th scope="col">Dostawca</th>
                <th scope="col">Wydarzenie</th>
                <th scope="col">Daty</th>
                {seesSums && (
                  <th scope="col" className="num">
                    Suma
                  </th>
                )}
                <th scope="col">Faktura</th>
                <th scope="col">Opłacona</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={key(r)} aria-label={`${r.supplierName}, ${r.eventName}`}>
                  <td>{r.supplierName}</td>
                  <td>
                    <Link to={`/wydarzenia?wydarzenie=${r.eventId}&zakladka=dopozyczenia`}>{r.eventName}</Link>
                    <span className="muted sup-count"> · {r.count === 1 ? '1 pozycja' : `${r.count} poz.`}</span>
                    {r.eventClosed && r.count > 0 && <span className="small rental-mark rental-mark-sprawdz sup-closed">{CLOSED_WARNINGS[r.eventClosed]}</span>}
                    {r.eventClosed && r.count === 0 && <span className="small rental-mark rental-mark-anulowane sup-closed">{CANCELLED_LABELS[r.eventClosed]} — sprawdź rozliczenie z dostawcą</span>}
                  </td>
                  <td>{formatDays(r.firstDay, r.lastDay).replace(' – ', '–').replace(/^(\d+) \w+–/, '$1–')}</td>
                  {seesSums && <td className="num">{r.totalGrosze !== undefined ? formatPln(r.totalGrosze) : ''}</td>}
                  <td>
                    {r.invoice.purchases.map((p) => (
                      <span key={p.id} className="sup-invoice">
                        {purchaseLabel(p)}
                        {r.invoice.state === 'reczna' && mayLink && (
                          <button className="btn small ghost" type="button" onClick={() => void unlink(r, p)}>
                            Odłącz
                          </button>
                        )}
                      </span>
                    ))}
                    <span className={`sup-state ${r.invoice.state}`}>{INVOICE[r.invoice.state]}</span>
                    {r.invoice.state === 'brak' && r.suggestions.length > 0 && <span className="muted sup-hint"> · są faktury do sprawdzenia ({r.suggestions.length})</span>}
                    {mayLink && r.invoice.state === 'auto' && (
                      <button className="btn small ghost" type="button" onClick={() => void dismiss(r, r.invoice.purchases[0]!)}>
                        To nie ta faktura
                      </button>
                    )}
                    {mayLink && r.invoice.state !== 'reczna' && linking !== key(r) && (
                      <button className="btn small" type="button" onClick={() => setLinking(key(r))}>
                        Połącz z fakturą
                      </button>
                    )}
                    {linking === key(r) && <LinkForm row={r} onDone={() => setLinking(null)} />}
                  </td>
                  <td>
                    {r.paid === null ? (
                      <span className="muted" title={data.data?.paidKnown ? 'Bez faktury nie ma płatności' : 'Płatności nie są jeszcze podłączone'}>
                        —
                      </span>
                    ) : r.paid === 'zaplacona' ? (
                      <span className="sup-paid">tak</span>
                    ) : (
                      <span className="sup-unpaid">nie</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
